/***************************************************************************************************
** useKinnFetch
***************************************************************************************************/

import { useMemo, useRef, useReducer, useCallback, useEffect } from "react";

import { handleStaleAppVersion } from "@utils";
import buildApiParams from "@utils/helpers/window/buildApiParams";
import buildUrl from "@utils/helpers/window/buildUrl";

const FETCHING = Symbol("FETCHING");
const FETCHED = Symbol("FETCHED");
const FETCH_ERROR = Symbol("FETCH_ERROR");

const INVALID_PATH_SEGMENTS = [
  undefined,
  "undefined",
  null,
  "null",
  "",
];

const initialState = {
  status: "idle",
  error: null,
  data: [],
};

export default function useKinnFetch(pathSegments = [], params = {}) {
  const url = useMemo(() => {
    if (pathSegments.some(s => INVALID_PATH_SEGMENTS.includes(s))) return null;

    const baseUrl = window.appContext.kinndomBaseUrl;
    const path = pathSegments.join("/");

    const apiParams = buildApiParams({ params: params });

    return buildUrl(`${baseUrl}/api/${path}`, apiParams);
  }, [pathSegments, params]);

  const dataCache = useRef({});

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
    case FETCHING:
      return {
        ...initialState,
        status: "fetching",
      };
    case FETCHED:
      return {
        ...initialState,
        status: "fetched",
        data: action.payload,
      };
    case FETCH_ERROR:
      return {
        ...initialState,
        status: "error",
        error: action.payload,
      };
    default:
      return state;
    }
  }, initialState);

  /***************************************************************************************************
  ** __fetchData
  ***************************************************************************************************/

  const __fetchData = useCallback(async (abortSignal) => {
    if (!url) return;

    dispatch({ type: FETCHING });

    if (dataCache.current[url]) {
      const data = dataCache.current[url];
      dispatch({ type: FETCHED, payload: data });
    } else {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Cache-Control": "no-cache, no-store",
            "Authorization-Token": window.appContext.authorizationToken,
          },
          signal: abortSignal,
        });

        const responseBody = await response.json().catch(() => ({ error: response.statusText }));

        handleStaleAppVersion(response);
        if (!response.ok) throw new Error(responseBody.error || response.statusText);

        const data = responseBody.records;

        dataCache.current[url] = data;

        dispatch({ type: FETCHED, payload: data });
      } catch (error) {
        if (error.name === "AbortError") return;

        dispatch({ type: FETCH_ERROR, payload: error.message });
      }
    }
  }, [url]);

  const __refetchData = useCallback(async (abortSignal) => {
    if (!url) return;

    // dispatch({ type: FETCHING });
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Cache-Control": "no-cache, no-store",
          "Authorization-Token": window.appContext.authorizationToken,
        },
        signal: abortSignal,
      });

      const responseBody = await response.json().catch(() => ({ error: response.statusText }));

      handleStaleAppVersion(response);
      if (!response.ok) throw new Error(responseBody.error || response.statusText);

      const data = responseBody.records;

      dataCache.current[url] = data;

      dispatch({ type: FETCHED, payload: data });
    } catch (error) {
      if (error.name === "AbortError") return;

      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  }, [url]);

  /***************************************************************************************************
  ** fetch
  ***************************************************************************************************/

  const fetchData = useCallback(() => {
    const abortController = new AbortController();
    __fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [__fetchData]);

  /***************************************************************************************************
  ** refetch
  ***************************************************************************************************/

  const refetchData = useCallback(() => {
    dataCache.current[url] = undefined;

    const abortController = new AbortController();
    __refetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [url, __refetchData]);

  /***************************************************************************************************
  ** Run fetchData on mount
  ***************************************************************************************************/

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**************************************************************************************************/

  return [
    state.data,
    state.status,
    refetchData,
    fetchData,
  ];

  // return {
  //   ...state,
  //   fetch: fetchData,
  //   refetch: refetchData,
  // };
}
